import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../App"
import { Localization, ChangeLogTexts } from "../App/Localization"
import { OverlayTitle } from "../Components/Overlay"
import { Block, BlockContainer } from "../Components/Block"
import { GetAppView } from "./AppView"
import { DeviceDebugTraceApi } from "../Types"
import { createLogger } from "../App/Logger"

const logger = createLogger("AboutView");

interface AboutViewProps {
}

export type UploadAppDataStatus = "idle" | "uploading" | "success" | "error";

export const AboutViewData = {
  // Newest changelog version key (sorted descending), or undefined when empty.
  currentVersion(changeLog?: ChangeLogTexts): string | undefined {
    const versions = Object.keys(changeLog || {}).toSorted().toReversed();
    if (versions && versions.length > 0) {
      return versions[0];
    }
    return undefined;
  },

  // Button label for the current upload-app-data status, falling back to the
  // idle label. Debounces re-clicks by keeping the button disabled/relabelled
  // while an upload is already in flight (@mutle's preferred pattern -- see
  // ShipSelectView's "Selecting…" debounce).
  uploadAppDataLabel(status: UploadAppDataStatus, t: (text: string) => string): string {
    switch (status) {
      case "uploading": return t("uploadAppDataUploading");
      case "success": return t("uploadAppDataSuccess");
      case "error": return t("uploadAppDataError");
      default: return t("uploadAppData");
    }
  },
};

export function AboutView(_: AboutViewProps) {
  const app = GetApp();
  const { resetAppOverlay } = GetAppView();
  const [_show, _setShow] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<UploadAppDataStatus>("idle");
  const t = app.getT("AboutView");

  const currentVersion = useMemo(() => {
    return AboutViewData.currentVersion(Localization.Messages[app.language].changeLog);
  }, []);

  // Uploads the current ship's local database (if a ship is selected), the
  // shared app-level database, and this shell's capped local log file (issue
  // #405) to the cloud for customer support, via the local app's own API
  // (only meaningful for a native session with a local database -- see the
  // `!app.cloudSession()` guard around the button below). The log isn't
  // scoped to any particular ship, so it's always sent alongside the app
  // database.
  // Sends this device's ship database (when a ship is selected), the shared
  // app-level database, this shell's capped local log file (issue #405) and any
  // crash log to the cloud for customer support, via the local app's own API
  // (only meaningful for a native session with a local database -- see the
  // `!app.cloudSession()` guard around the button below).
  //
  // One request, not one per artifact: the client used to sequence three calls
  // and was observed to stop after the two databases, so support received no
  // log at all. The route now collects every artifact server-side and reports
  // which ones failed, so a partial upload surfaces here instead of silently
  // dropping the rest.
  const uploadAppData = useCallback(async () => {
    if (uploadStatus === "uploading") {
      return;
    }
    setUploadStatus("uploading");
    try {
      const api = new DeviceDebugTraceApi(app.apiClient);
      const result = await api.debugUpload.upload({ params: {} }).call();
      if (!result.success) {
        logger.error("App data upload incomplete", result.failed);
        setUploadStatus("error");
        return;
      }
      setUploadStatus("success");
    } catch (error) {
      logger.error("App data upload failed", error);
      setUploadStatus("error");
    }
  }, [app, uploadStatus]);

  return (
    <>
      <OverlayTitle title={t("title")} closeCallback={resetAppOverlay} />
      <BlockContainer className="AboutView Overlay-body">
        <Block className="AboutView-content">
          <div className="text-centered">
            <span className="ShipView-logo" style={{ background: `url('/icon${app.theme == "light" ? "-black" : app.theme == "night" ? "-red" : ""}.svg') center center no-repeat`, display: "inline-block", width: "64px", height: "64px", backgroundSize: "contain" }}></span>
          </div>
          <h2 className="text-centered">LOGBOOK</h2>
          <p className="text-centered">{t("version")}: {app.t("LogbookApp_version_major")} {currentVersion && <span>({currentVersion})</span>}</p>
          <p className="text-centered">{t("copyright")}</p>
          {/* <p>
          Maritime logbook application for modern vessels.
        </p> */}
          {/* <BlockRow> */}
          <p className="text-centered"><strong>{t("website")}:</strong> <a href="https://trimutic.com" target="_blank" rel="noreferrer">trimutic.com/</a></p>
          <p className="text-centered"><strong>{t("support")}:</strong> <a href="mailto:support@trimutic.com" target="_blank" rel="noreferrer">support@trimutic.com</a></p>
          {/* </BlockRow> */}
          {!app.cloudSession() && (
            <div className="text-centered AboutView-upload-app-data">
              <button
                type="button"
                className="button"
                disabled={uploadStatus == "uploading"}
                onClick={uploadAppData}
              >
                {AboutViewData.uploadAppDataLabel(uploadStatus, t)}
              </button>
            </div>
          )}
        </Block>
      </BlockContainer>
    </>
  );
}
